/** Header bell: unread badge + dropdown list with mark-as-read actions. */
import React, { useState } from "react";
import * as Popover from "@radix-ui/react-popover";
import { Bell, CheckCheck } from "lucide-react";
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from "../api/notifications";
import { getRelativeTime } from "../utils/helpers";
import { Spinner } from "./ui/Spinner";
import clsx from "clsx";

export const NotificationsMenu: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { data: unread = 0 } = useUnreadCount();
  // Only fetch the list while the panel is open.
  const { data, isLoading } = useNotifications(
    open ? { limit: 10 } : undefined,
  );
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const items = data?.items ?? [];

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          className="relative rounded-lg p-2 text-slate-300 transition-colors hover:bg-slate-800"
          aria-label="Notifications"
        >
          <Bell size={20} />
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={8}
          className="z-50 w-80 overflow-hidden rounded-xl border border-slate-700 bg-slate-800 shadow-2xl"
        >
          <div className="flex items-center justify-between border-b border-slate-700 px-4 py-3">
            <span className="text-sm font-semibold text-slate-100">
              Notifications
            </span>
            {unread > 0 && (
              <button
                onClick={() => markAll.mutate()}
                className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
              >
                <CheckCheck size={14} /> Mark all read
              </button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner className="text-blue-500" />
              </div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-400">
                You're all caught up.
              </p>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  onClick={() => !n.isRead && markRead.mutate(n.id)}
                  className={clsx(
                    "flex w-full flex-col gap-0.5 border-b border-slate-700/60 px-4 py-3 text-left transition-colors hover:bg-slate-700/40",
                    !n.isRead && "bg-blue-500/5",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-100">
                      {n.title}
                    </span>
                    {!n.isRead && (
                      <span className="h-2 w-2 shrink-0 rounded-full bg-blue-500" />
                    )}
                  </div>
                  <span className="text-xs text-slate-400">{n.message}</span>
                  <span className="mt-0.5 text-[11px] text-slate-500">
                    {getRelativeTime(n.createdAt)}
                  </span>
                </button>
              ))
            )}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
